package com.sweetlime.bingeamigo.presentation.features.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.LibraryMovie
import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.model.UserProfile
import com.sweetlime.bingeamigo.presentation.components.MovieCard
import com.sweetlime.bingeamigo.presentation.components.UserActivityCard
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.service.XpService
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource

private val Line = Color(0xFF2A2A2E)
private val Teal = Color(0xFF2FD0D0)

private val diceBearSeeds = listOf(
    "Kubrick", "Hitchcock", "Spielberg", "Tarantino", "Scorsese", "Nolan",
    "Joker", "Vader", "Ripley", "Hannibal", "Maximus", "HAL9000",
    "Reel", "Auteur", "Clapboard", "Marquee", "Cinephile", "Montage",
    "Noir", "Thriller", "Odyssey", "Phantom", "Vertigo", "Chinatown",
    "Binger", "Watcher", "Critic", "Director", "Lynch", "Coppola",
)
private fun diceBearUrl(seed: String) =
    "https://api.dicebear.com/9.x/pixel-art/png?seed=$seed&size=200"

@Composable
fun ProfileContent(
    profile: UserProfile?,
    email: String = "",
    followers: Int,
    following: Int,
    favorites: List<LibraryMovie> = emptyList(),
    communityRatings: Map<Int, Double> = emptyMap(),
    activity: List<UserActivity> = emptyList(),
    isSavingUsername: Boolean = false,
    usernameError: String? = null,
    isSavingAvatar: Boolean = false,
    onOpenSettings: () -> Unit = {},
    onXpClick: () -> Unit = {},
    onFollowersClick: () -> Unit = {},
    onFollowingClick: () -> Unit = {},
    onFavoriteClick: (LibraryMovie) -> Unit = {},
    onActivityClick: (UserActivity) -> Unit = {},
    onEditUsername: (String, () -> Unit) -> Unit = { _, _ -> },
    onUpdateAvatar: (String, () -> Unit) -> Unit = { _, _ -> },
    modifier: Modifier = Modifier,
) {
    val name = profile?.username?.ifBlank { null } ?: "Movie Lover"
    val level = profile?.level ?: 1
    val xp = profile?.xp ?: 0
    val levelName = XpService.levelName(level)
    val currentLevelXp = XpService.xpRequired(level)
    val nextLevelXp = XpService.xpRequired(level + 1)
    val xpInLevel = (xp - currentLevelXp).coerceAtLeast(0)
    val xpForLevel = (nextLevelXp - currentLevelXp).coerceAtLeast(1)
    val pct = (xpInLevel.toFloat() / xpForLevel.toFloat()).coerceIn(0f, 1f)
    val avatarUrl = profile?.avatarUrl?.ifBlank { null }

    var showEditDialog by remember { mutableStateOf(false) }
    var showAvatarPicker by remember { mutableStateOf(false) }
    var editedUsername by remember(name) { mutableStateOf(name) }

    if (showEditDialog) {
        AlertDialog(
            onDismissRequest = { if (!isSavingUsername) showEditDialog = false },
            title = { Text("Edit username", color = Color.White) },
            text = {
                Column {
                    OutlinedTextField(
                        value = editedUsername,
                        onValueChange = { editedUsername = it },
                        singleLine = true,
                        placeholder = { Text("Username") },
                        isError = usernameError != null,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = BrandColors.Secondary,
                            unfocusedBorderColor = Color.White.copy(alpha = 0.3f),
                            focusedTextColor = Color.White,
                            unfocusedTextColor = Color.White,
                            cursorColor = BrandColors.Secondary,
                            errorBorderColor = BrandColors.Error,
                        ),
                    )
                    usernameError?.let {
                        Spacer(Modifier.height(6.dp))
                        Text(it, color = BrandColors.Error, style = MaterialTheme.typography.bodySmall)
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = { onEditUsername(editedUsername) { showEditDialog = false } },
                    enabled = !isSavingUsername,
                    colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Secondary),
                ) {
                    if (isSavingUsername)
                        CircularProgressIndicator(modifier = Modifier.size(16.dp), strokeWidth = 2.dp, color = Color.White)
                    else
                        Text("Save", color = Color.White)
                }
            },
            dismissButton = {
                TextButton(onClick = { showEditDialog = false }, enabled = !isSavingUsername) {
                    Text("Cancel", color = Color.White.copy(alpha = 0.7f))
                }
            },
            containerColor = BrandColors.Surface,
            shape = RoundedCornerShape(20.dp),
        )
    }

    if (showAvatarPicker) {
        AvatarPickerDialog(
            currentAvatarUrl = profile?.avatarUrl ?: "",
            isSaving = isSavingAvatar,
            onSelect = { url -> onUpdateAvatar(url) { showAvatarPicker = false } },
            onDismiss = { if (!isSavingAvatar) showAvatarPicker = false },
        )
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(BrandColors.Background)
            .verticalScroll(rememberScrollState())
            .padding(top = 16.dp, bottom = 100.dp),
    ) {
        // ── Top section (padded) ─────────────────────────────────────────
        Column(Modifier.padding(horizontal = 16.dp)) {
            Box(Modifier.fillMaxWidth()) {
                IconButton(
                    onClick = onOpenSettings,
                    modifier = Modifier.align(Alignment.TopEnd),
                ) {
                    Icon(Icons.Filled.Settings, "Settings", tint = BrandColors.TextSecondary)
                }
            }

            // Avatar + name + email
            Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                Box(Modifier.size(84.dp)) {
                    Box(
                        Modifier.fillMaxSize().clip(CircleShape).clickable { showAvatarPicker = true },
                        contentAlignment = Alignment.Center,
                    ) {
                        if (avatarUrl != null) {
                            KamelImage(
                                resource = asyncPainterResource(avatarUrl),
                                contentDescription = "Avatar",
                                contentScale = ContentScale.Crop,
                                modifier = Modifier.fillMaxSize(),
                            )
                        } else {
                            Box(
                                Modifier.fillMaxSize().background(
                                    Brush.linearGradient(listOf(Color(0xFFFFCF4D), Color(0xFFFF8A3D)))
                                ),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(name.take(1).uppercase(), color = Color(0xFF1A1206), fontWeight = FontWeight.ExtraBold, fontSize = 34.sp)
                            }
                        }
                    }
                    Box(
                        Modifier
                            .size(24.dp)
                            .align(Alignment.BottomEnd)
                            .clip(CircleShape)
                            .background(BrandColors.Secondary),
                        contentAlignment = Alignment.Center,
                    ) {
                        Icon(Icons.Filled.Edit, "Edit avatar", tint = Color.White, modifier = Modifier.size(13.dp))
                    }
                }

                Spacer(Modifier.height(12.dp))

                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    Text(name, color = Color.White, fontWeight = FontWeight.ExtraBold, fontSize = 23.sp)
                    IconButton(onClick = { showEditDialog = true }, modifier = Modifier.size(26.dp)) {
                        Icon(Icons.Filled.Edit, "Edit username", tint = BrandColors.TextSecondary, modifier = Modifier.size(15.dp))
                    }
                }
                Spacer(Modifier.height(2.dp))
                Text(email, color = BrandColors.TextSecondary, fontSize = 13.5.sp)
            }

            Spacer(Modifier.height(18.dp))

            // XP / level card
            ProfileCard(onClick = onXpClick) {
                Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                    Text("Level $level", color = Color.White, fontWeight = FontWeight.ExtraBold, fontSize = 15.sp)
                    Spacer(Modifier.height(2.dp))
                    Text(levelName, color = Teal, fontWeight = FontWeight.Bold, fontSize = 13.5.sp)
                    Spacer(Modifier.height(12.dp))
                    LinearProgressIndicator(
                        progress = { pct },
                        modifier = Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(4.dp)),
                        color = BrandColors.Secondary,
                        trackColor = Color.White.copy(alpha = 0.13f),
                    )
                    Spacer(Modifier.height(8.dp))
                    Text("$xpInLevel / $xpForLevel XP", color = BrandColors.TextSecondary, fontSize = 12.5.sp)
                }
            }

            Spacer(Modifier.height(12.dp))

            // Followers / following
            ProfileCard {
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    StatCol(followers.toString(), "Followers", Modifier.weight(1f).clickable(onClick = onFollowersClick))
                    Box(Modifier.width(1.dp).height(34.dp).background(Line))
                    StatCol(following.toString(), "Following", Modifier.weight(1f).clickable(onClick = onFollowingClick))
                }
            }

        }

        // ── Favorites ────────────────────────────────────────────────────
        if (favorites.isNotEmpty()) {
            Spacer(Modifier.height(28.dp))
            Text(
                "Favorites",
                color = Color.White,
                fontWeight = FontWeight.ExtraBold,
                fontSize = 19.sp,
                modifier = Modifier.padding(start = 16.dp, bottom = 14.dp),
            )
            LazyRow(
                contentPadding = PaddingValues(horizontal = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                items(favorites.take(10)) { movie ->
                    MovieCard(
                        libraryMovie = movie,
                        modifier = Modifier.width(110.dp),
                        communityRating = communityRatings[movie.id],
                        onClick = { onFavoriteClick(movie) },
                    )
                }
            }
        }

        // ── Recent Activity ──────────────────────────────────────────────
        if (activity.isNotEmpty()) {
            Spacer(Modifier.height(28.dp))
            Text(
                "Recent Activity",
                color = Color.White,
                fontWeight = FontWeight.ExtraBold,
                fontSize = 19.sp,
                modifier = Modifier.padding(start = 16.dp, bottom = 4.dp),
            )
            activity.forEach { entry ->
                UserActivityCard(activity = entry, showUsername = false, onClick = { onActivityClick(it) })
            }
        }
    }
}

@Composable
private fun ProfileCard(onClick: (() -> Unit)? = null, content: @Composable () -> Unit) {
    val clickMod = if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier
    Box(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(18.dp))
            .background(BrandColors.Card)
            .border(1.dp, Line, RoundedCornerShape(18.dp))
            .then(clickMod)
            .padding(16.dp),
    ) { content() }
}

@Composable
private fun StatCol(value: String, label: String, modifier: Modifier = Modifier) {
    Column(modifier.padding(vertical = 4.dp), horizontalAlignment = Alignment.CenterHorizontally) {
        Text(value, color = Color.White, fontWeight = FontWeight.ExtraBold, fontSize = 18.sp)
        Text(label, color = BrandColors.TextSecondary, fontSize = 12.sp)
    }
}


@Composable
private fun AvatarPickerDialog(
    currentAvatarUrl: String,
    isSaving: Boolean,
    onSelect: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = { if (!isSaving) onDismiss() },
        title = { Text("Choose avatar", color = Color.White) },
        text = {
            LazyVerticalGrid(
                columns = GridCells.Fixed(3),
                modifier = Modifier.height(360.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                items(diceBearSeeds) { seed ->
                    val url = diceBearUrl(seed)
                    val isSelected = currentAvatarUrl == url
                    Box(
                        modifier = Modifier
                            .aspectRatio(1f)
                            .clip(CircleShape)
                            .then(if (isSelected) Modifier.border(3.dp, BrandColors.Secondary, CircleShape) else Modifier)
                            .clickable(enabled = !isSaving) { onSelect(url) },
                    ) {
                        KamelImage(
                            resource = asyncPainterResource(url),
                            contentDescription = seed,
                            contentScale = ContentScale.Crop,
                            modifier = Modifier.fillMaxSize(),
                        )
                        if (isSaving && isSelected) {
                            Box(
                                modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.5f)),
                                contentAlignment = Alignment.Center,
                            ) {
                                CircularProgressIndicator(modifier = Modifier.size(24.dp), color = Color.White, strokeWidth = 2.dp)
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !isSaving) {
                Text("Cancel", color = Color.White.copy(alpha = 0.7f))
            }
        },
        containerColor = BrandColors.Surface,
        shape = RoundedCornerShape(20.dp),
    )
}
